import React, { Fragment, useRef, useState } from "react";

import {
  Avatar,
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { useHistory } from "react-router";
import { useSnackbar } from "notistack";

import { AA_BRAND_NAME, BSM_BRAND_NAME } from "@ds-developer/constants";
import { IS_COGNITO_AUTH_ENABLED } from "@hubcapp/constants";
import { getInitials } from "@ds-developer/frontend";

import { Brand } from "@hubcapp/types";
import { getBrandRoute } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

const useStyles = (brands: Brand[]) =>
  makeStyles(theme => {
    let backgroundColor = theme.palette.brand[AA_BRAND_NAME];
    if (brands.includes(Brand.Aads) && brands.includes(Brand.Bsm)) {
      backgroundColor = "#bdbdbd";
    } else if (brands.includes(Brand.Bsm)) {
      backgroundColor = theme.palette.brand[BSM_BRAND_NAME];
    }

    return {
      avatar: {
        backgroundColor,
      },
      popover: {
        width: 200,
      },
    };
  });

const Account: React.FC = () => {
  const {
    instructor: { brands, firstName, lastName },
    randomSignInSignOut,
    signOut,
  } = useAuth();
  const classes = useStyles(brands)();
  const history = useHistory();
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState(false);
  const { trackEvent } = useTealium();

  const username = `${firstName} ${lastName}`;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    trackEvent({
      eventCategory: "Nav Bar",
      eventAction: "Interaction",
      eventLabel: "Log Out",
    });
    try {
      handleClose();
      const signOutMethod = IS_COGNITO_AUTH_ENABLED
        ? signOut
        : randomSignInSignOut;
      signOutMethod().then(() => {
        history.push(`/${getBrandRoute(brands)}/sign-in?doLogout=true`);
      });
    } catch (error) {
      enqueueSnackbar("Unable to logout", {
        variant: "error",
      });
    }
  };

  return (
    <Fragment>
      <Box
        display="flex"
        alignItems="center"
        component={ButtonBase}
        onClick={handleOpen}
        // @ts-ignore
        ref={ref}
      >
        <Avatar
          alt="User"
          className={classes.avatar}
          data-test="topbar-account-avatar"
        >
          {getInitials(username)}
        </Avatar>
        <Box pl={1} display={{ xs: "none", lg: "block" }}>
          <Typography
            variant="h6"
            color="inherit"
            data-test="topbar-account-username"
          >
            {username}
          </Typography>
        </Box>
      </Box>
      <Menu
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        keepMounted
        PaperProps={{ className: classes.popover }}
        getContentAnchorEl={null}
        anchorEl={ref.current}
        open={isOpen}
      >
        <MenuItem
          component={RouterLink}
          to="/settings/my-details"
          onClick={() => {
            handleClose();
            trackEvent({
              eventCategory: "Nav Bar",
              eventAction: "Interaction",
              eventLabel: "My Details",
            });
          }}
        >
          My Details
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={`/${getBrandRoute(brands)}/change-password`}
          onClick={() => {
            handleClose();
            trackEvent({
              eventCategory: "Nav Bar",
              eventAction: "Interaction",
              eventLabel: "Change Password",
            });
          }}
        >
          Change Password
        </MenuItem>
        <MenuItem data-test="logout-menuitem" onClick={handleLogout}>
          Logout
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

export default Account;
